import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../i18n';
import { QRCodeDisplay, AppIcon } from '../shared';
import { RoomCodeDisplay } from './RoomCodeDisplay';

export type PremiumButtonAnim = 'zoom' | 'jump' | 'shake' | null;

interface LobbyQrPanelProps {
  roomName: string;
  isPremium: boolean;
  hostIsPlaying: boolean;
  premiumButtonAnim: PremiumButtonAnim;
  brandingDisplayName?: string;
  brandingLogoUrl?: string;
  onZoomQr: () => void;
  onOpenPremium: () => void;
  onOpenSettings?: () => void;
  onJoinRoom?: () => void;
  onToggleHostPlaying: () => void;
}

function prefersHover(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
}

export const LobbyQrPanel: React.FC<LobbyQrPanelProps> = ({
  roomName,
  isPremium,
  hostIsPlaying,
  premiumButtonAnim,
  brandingDisplayName,
  brandingLogoUrl,
  onZoomQr,
  onOpenPremium,
  onOpenSettings,
  onJoinRoom,
  onToggleHostPlaying,
}) => {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);
  const infoPinnedRef = useRef(false);
  const infoWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!infoOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (infoWrapRef.current?.contains(event.target as Node)) return;
      infoPinnedRef.current = false;
      setInfoOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      infoPinnedRef.current = false;
      setInfoOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [infoOpen]);

  const openInfo = () => setInfoOpen(true);
  const closeInfoIfUnpinned = () => {
    if (!infoPinnedRef.current) setInfoOpen(false);
  };
  const toggleInfo = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    infoPinnedRef.current = !infoPinnedRef.current;
    setInfoOpen(infoPinnedRef.current);
  };

  return (
    <div className="host-qr-column">
      <div className="host-qr-layout">
        <div className="host-qr-layout__code">
          <div className="host-qr-code-wrapper">
            <QRCodeDisplay roomName={roomName} size={160} showLabel={false} bare />
            <button
              type="button"
              className="host-qr-zoom-button host-qr-zoom-button--overlay"
              onClick={onZoomQr}
              aria-label={t('lobby.gameSelection.zoomQrCode')}
            >
              <AppIcon name="zoomIn" />
            </button>
          </div>
        </div>

        <div className="host-qr-layout__details">
          {(brandingLogoUrl || brandingDisplayName) && (
            <div className="host-qr-branding" data-testid="room-branding">
              {brandingLogoUrl && (
                <img src={brandingLogoUrl} alt="" className="host-qr-branding__logo" />
              )}
              {brandingDisplayName && (
                <div className="host-qr-branding__name">{brandingDisplayName}</div>
              )}
            </div>
          )}
          <div className="host-qr-room-code-top">
            <div className="host-qr-room-code-label-row">
              <span>{t('lobby.gameSelection.roomCodeLabel')}</span>
              <RoomCodeDisplay code={roomName} />
            </div>
          </div>
          <p className="host-qr-room-code-hint">
            {t('lobby.gameSelection.shareRoomCodeHint')}
          </p>
          {isPremium && (
            <div className="host-qr-premium-meta">
              <div className="host-qr-premium-label">
                {t('lobby.gameSelection.premiumRoomLabel')}
              </div>
              {onOpenSettings && (
                <button
                  type="button"
                  className="host-qr-customize-link"
                  data-testid="lobby-customize-room"
                  onClick={onOpenSettings}
                >
                  {t('lobby.gameSelection.customizeRoom')}
                </button>
              )}
            </div>
          )}
          {onJoinRoom && !isPremium && (
            <div className="host-qr-premium-actions">
              <button
                type="button"
                className={`host-qr-premium-card host-qr-premium-card--primary${
                  premiumButtonAnim ? ` host-qr-premium-card--anim-${premiumButtonAnim}` : ''
                }`}
                onClick={onOpenPremium}
              >
                <span className="host-qr-premium-card__row">
                  <AppIcon name="premium" fontSize="small" />
                  {t('lobby.gameSelection.getPremium')}
                </span>
                <span className="host-qr-premium-card__sub">
                  {t('lobby.gameSelection.unlockFromPrice')}
                </span>
              </button>
              <button
                type="button"
                className="host-qr-premium-card host-qr-premium-card--secondary"
                onClick={onJoinRoom}
              >
                <span className="host-qr-premium-card__row">
                  {t('lobby.gameSelection.haveCode')}
                </span>
                <span className="host-qr-premium-card__sub">
                  {t('lobby.gameSelection.enterPremiumCode')}
                </span>
              </button>
            </div>
          )}
        </div>

        <div className="host-qr-layout__footer">
          <p className="host-qr-share-hint">
            <AppIcon name="tv" fontSize="small" />
            {t('lobby.gameSelection.shareHint')}
          </p>
          <div className={`host-playing-toggle host-qr-playing-toggle ${hostIsPlaying ? 'active' : ''}`}>
            <div className="host-playing-toggle-header" onClick={onToggleHostPlaying}>
              <div className="host-playing-toggle-content">
                <div className="host-playing-toggle-label">
                  <AppIcon name="gamepad" fontSize="small" />
                  <span>{t('lobby.gameSelection.playAsHostOnMobile')}</span>
                  <div
                    className="host-playing-info"
                    ref={infoWrapRef}
                    onMouseEnter={() => {
                      if (prefersHover()) openInfo();
                    }}
                    onMouseLeave={() => {
                      if (prefersHover()) closeInfoIfUnpinned();
                    }}
                  >
                    <button
                      type="button"
                      className="host-playing-info__button"
                      aria-label={t('lobby.gameSelection.playAsHostOnMobileInfoAria')}
                      aria-expanded={infoOpen}
                      aria-controls="host-playing-info-tooltip"
                      onClick={toggleInfo}
                    >
                      <AppIcon name="info" fontSize="inherit" />
                    </button>
                    {infoOpen && (
                      <div
                        id="host-playing-info-tooltip"
                        className="host-playing-info__tooltip"
                        role="tooltip"
                      >
                        <p>{t('lobby.gameSelection.playAsHostOnMobileInfoP1')}</p>
                        <p>{t('lobby.gameSelection.playAsHostOnMobileInfoP2')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={`host-playing-toggle-switch ${hostIsPlaying ? 'active' : ''}`}>
                <div className="host-playing-toggle-switch-thumb" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
